// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { EditablePropertyButton } from '@/components/editable-property-button';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { PopoverForm } from '@/components/popover-form';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  Currency,
  SerializableCurrency,
  clearableCurrencyInputSchema,
} from '@/currency/api/types';
import { toCurrencyInputValue } from '@/currency/utilities/to-form-value';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTimeout } from '@/hooks/use-timeout';
import { Project } from '@/project/api/types';
import { ProjectTotalContractValueLabel } from '@/project/components/project-total-contract-value-label';
import { useUpdateProject } from '@/project/hooks/use-update-project';
import {
  PROJECT_INVOICED_CHARGE_LOCKED_TOOLTIP,
  deriveProjectCurrency,
  lineItemCurrencies,
  projectCurrencyLockedTooltip,
} from '@/project/utilities/project-currency';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTheme } from '@mui/material';
import { useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  totalContractValue: clearableCurrencyInputSchema,
});

interface FormInput {
  totalContractValue: SerializableCurrency;
}

type FormOutput = z.output<typeof formSchema>;

interface Props {
  project: Pick<
    Project,
    'key' | 'totalContractValue' | 'discount' | 'items' | 'currency'
  >;
}

export function ProjectTotalContractValueControl(props: Props) {
  const theme = useTheme();
  const editor = useDisclosure();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { user } = useCurrentUser();

  // The contract value adopts the currency already locked for the project: an
  // invoiced charge freezes it regardless of which field set it; otherwise it
  // locks to the currency another field (the discount or a line item) set,
  // staying editable only when the contract value is the sole currency source.
  const projectCurrency =
    props.project.currency?.source === 'INVOICED_CHARGE'
      ? props.project.currency.isoCode
      : deriveProjectCurrency([
          props.project.discount,
          ...lineItemCurrencies(props.project.items),
        ]);
  const projectCurrencyTooltip = projectCurrency
    ? props.project.currency?.source === 'INVOICED_CHARGE'
      ? PROJECT_INVOICED_CHARGE_LOCKED_TOOLTIP
      : projectCurrencyLockedTooltip(projectCurrency)
    : undefined;

  const toFormValue = (value: Currency | null): SerializableCurrency =>
    toCurrencyInputValue(
      value,
      projectCurrency ?? user.organization.defaultCurrency
    );

  const { control, reset, setError, handleSubmit } = useForm<
    FormInput,
    unknown,
    FormOutput
  >({
    resolver: zodResolver(formSchema),
    defaultValues: {
      totalContractValue: toFormValue(props.project.totalContractValue),
    },
  });

  const updateProject = useUpdateProject(props.project.key, {
    onSuccess: (project) => {
      reset({ totalContractValue: toFormValue(project.totalContractValue) });
      editor.close();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      if (apiError.errorType === 'project_mixed_currency') {
        setError('totalContractValue.isoCode', {
          type: 'server',
          message: getUserFacingErrorMessage(apiError),
        });
      }
    },
  });

  // The mixed-currency rejection renders inline on the field; other errors fall
  // back to the support snackbar.
  const isMixedCurrencyError =
    updateProject.error !== null &&
    handleAPIError(updateProject.error).errorType === 'project_mixed_currency';

  const timeout = useTimeout(reset, theme.transitions.duration.short);

  const handleClose = () => {
    editor.close();
    timeout.start();
  };

  const handleValidSubmission: SubmitHandler<FormOutput> = (data) => {
    const current = props.project.totalContractValue;
    const next = data.totalContractValue;
    const unchanged =
      next === null
        ? current === null
        : current !== null &&
          next.amount.equals(current.amount) &&
          next.isoCode === current.isoCode;
    if (unchanged) {
      return editor.close();
    }

    updateProject.mutate({
      totalContractValue: next,
    });
  };

  return (
    <>
      <EditablePropertyButton ref={buttonRef} onClick={editor.open}>
        <ProjectTotalContractValueLabel
          value={props.project.totalContractValue}
        />
      </EditablePropertyButton>
      <SupportSnackbar
        isOpen={updateProject.isError && !isMixedCurrencyError}
        onClose={updateProject.reset}
        message="An error occurred while updating the project."
      />
      <PopoverForm
        position="left"
        anchorElement={buttonRef.current}
        isOpen={editor.isOpen}
        onCancel={handleClose}
        onSubmit={handleSubmit(handleValidSubmission)}
      >
        <FormCurrencyTextField
          name="totalContractValue"
          placeholder="0.00"
          control={control}
          lockTo={projectCurrency ?? undefined}
          disabledTooltip={projectCurrencyTooltip}
        />
      </PopoverForm>
    </>
  );
}
